/**
 * "Titreşim" (nudge) receiver: shakes the screen, buzzes, vibrates the phone where supported, shows
 * "X seni bekliyor" with a link to the room, and raises a system notification when the tab is in the
 * background (only if the user allowed notifications). Nothing is stored.
 */
import { useEffect, useRef, useState } from 'react';
import type { ServerEvent } from '@converoom/contracts';
import type { Realtime } from '../lib/realtime';
import { navigate } from '../lib/router';
import {
  NUDGE_PRESETS,
  clearCustomSound,
  customSoundName,
  getNudgePreset,
  playNudge,
  setCustomSound,
  setNudgePreset,
  type NudgePreset,
} from '../lib/sounds';

type NudgeEvent = Extract<ServerEvent, { t: 'nudge' }>;

export function NudgeAlert({ rt }: { rt: Realtime }) {
  const [nudge, setNudge] = useState<NudgeEvent | null>(null);
  const titleTimer = useRef<number | null>(null);

  useEffect(() => {
    const original = document.title;
    const stopTitle = () => {
      if (titleTimer.current) window.clearInterval(titleTimer.current);
      titleTimer.current = null;
      document.title = original;
    };
    const off = rt.on((e: ServerEvent) => {
      if (e.t !== 'nudge') return;
      setNudge(e);
      const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      if (!reduce) {
        document.body.classList.remove('nudge-shake');
        void document.body.offsetWidth; // restart the animation
        document.body.classList.add('nudge-shake');
        window.setTimeout(() => document.body.classList.remove('nudge-shake'), 700);
      }
      void playNudge();
      navigator.vibrate?.([150, 80, 150, 80, 250]);
      if (document.hidden) {
        let on = false;
        stopTitle();
        titleTimer.current = window.setInterval(() => {
          on = !on;
          document.title = on ? `📳 ${e.fromName} seni bekliyor` : original;
        }, 1000);
        if ('Notification' in window && Notification.permission === 'granted') {
          const n = new Notification(`${e.fromName} seni bekliyor`, {
            body: `${e.projectName} · ${e.roomName}`,
            tag: `nudge-${e.fromUserId}`,
          });
          n.onclick = () => {
            window.focus();
            navigate(`/p/${e.projectId}/r/${e.roomId}`);
            setNudge(null);
            n.close();
          };
        }
      }
    });
    const onVisible = () => {
      if (!document.hidden) stopTitle();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      off();
      document.removeEventListener('visibilitychange', onVisible);
      stopTitle();
    };
  }, [rt]);

  if (!nudge) return null;
  return (
    <div className="nudge" role="alertdialog" aria-labelledby="nudge-title" aria-describedby="nudge-room">
      <div className="nudge-icon" aria-hidden="true">
        📳
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <strong id="nudge-title">{nudge.fromName} seni bekliyor</strong>
        <div id="nudge-room" className="hint">
          Ekip arkadaşın oturumda: {nudge.projectName} · {nudge.roomName}
        </div>
      </div>
      <button
        className="btn primary"
        autoFocus
        onClick={() => {
          navigate(`/p/${nudge.projectId}/r/${nudge.roomId}`);
          setNudge(null);
        }}
      >
        Odaya git
      </button>
      <button className="btn ghost" onClick={() => setNudge(null)} aria-label="Uyarıyı kapat">
        Kapat
      </button>
    </div>
  );
}

/** Asks for desktop-notification permission (must be called from a click). */
export function NotificationOptIn() {
  const supported = typeof window !== 'undefined' && 'Notification' in window;
  const [perm, setPerm] = useState(supported ? Notification.permission : 'denied');
  if (!supported || perm !== 'default') return null;
  return (
    <button
      className="btn small ghost"
      onClick={async () => setPerm(await Notification.requestPermission())}
      style={{ marginTop: 'var(--s1)' }}
    >
      Arka plandayken titreşim bildirimi göster
    </button>
  );
}

/** Per-device nudge sound: built-in presets or the user's own file (kept only in this browser). */
export function NudgeSoundPicker() {
  const [preset, setPreset] = useState<NudgePreset>(() => getNudgePreset());
  const [customName, setCustomName] = useState<string | null>(() => customSoundName());
  const [msg, setMsg] = useState<string | null>(null);
  const file = useRef<HTMLInputElement>(null);

  const choose = (p: NudgePreset) => {
    setPreset(p);
    setNudgePreset(p);
    void playNudge(p);
  };

  return (
    <details className="nudge-sound">
      <summary>Titreşim sesi</summary>
      <div role="radiogroup" aria-label="Titreşim sesi" className="nudge-sound-list">
        {NUDGE_PRESETS.map((p) => (
          <label key={p.id}>
            <input type="radio" name="nudge-sound" checked={preset === p.id} onChange={() => choose(p.id)} />
            {p.label}
          </label>
        ))}
        {customName && (
          <label>
            <input
              type="radio"
              name="nudge-sound"
              checked={preset === 'custom'}
              onChange={() => choose('custom')}
            />
            Kendi sesim: {customName}
          </label>
        )}
      </div>
      <div className="row-actions" style={{ justifyContent: 'flex-start' }}>
        <button type="button" className="btn small ghost" onClick={() => void playNudge(preset)}>
          ▶ Dinle
        </button>
        <button type="button" className="btn small ghost" onClick={() => file.current?.click()}>
          Kendi ses dosyamı seç…
        </button>
        {customName && (
          <button
            type="button"
            className="btn small ghost"
            onClick={() => {
              clearCustomSound();
              setCustomName(null);
              setPreset(getNudgePreset());
            }}
          >
            Kendi sesimi kaldır
          </button>
        )}
      </div>
      <input
        ref={file}
        type="file"
        hidden
        accept="audio/*,.wav,.mp3,.ogg,.m4a"
        data-testid="nudge-sound-input"
        onChange={async (e) => {
          const f = e.target.files?.[0];
          e.target.value = '';
          if (!f) return;
          const r = await setCustomSound(f);
          if (r === 'ok') {
            setCustomName(customSoundName());
            setPreset('custom');
            setMsg(null);
            void playNudge('custom');
          } else
            setMsg(
              r === 'too_large'
                ? 'Dosya en fazla 1 MB olabilir.'
                : r === 'not_audio'
                  ? 'Bu dosya çalınamadı; WAV, MP3 veya OGG deneyin.'
                  : 'Tarayıcı bu dosyayı saklayamadı.',
            );
        }}
      />
      {msg && (
        <p className="error-text" role="alert">
          {msg}
        </p>
      )}
      <p className="hint">
        Seçim yalnızca bu cihazda geçerli. Kendi ses dosyanız sadece bu tarayıcıda saklanır ve yalnızca size
        çalınır; sunucuya gönderilmez.
      </p>
    </details>
  );
}
